import {
  isInstallBeforeRequiredLead,
  resolveProductsLeadTime,
} from './installationLeadTime';

/** True when rush fee should be applied on invoice/details/PDF. */
export function isRushFeeOn({ rushFee, rushFeeAmount, rushFeeEnabled } = {}) {
  // Explicit parent override (e.g. override-permission toggle saved OFF)
  if (rushFeeEnabled === false || rushFeeEnabled === 0 || rushFeeEnabled === '0') {
    return false;
  }
  if (
    rushFeeEnabled === true ||
    rushFeeEnabled === 1 ||
    rushFeeEnabled === '1' ||
    Number(rushFeeEnabled) === 1
  ) {
    return true;
  }
  // Handle 1 / '1' / true, and avoid treating formik `0` as blocking details
  if (
    rushFee === true ||
    rushFee === 1 ||
    rushFee === '1' ||
    Number(rushFee) === 1
  ) {
    return true;
  }
  return Number(rushFeeAmount) > 0;
}

/**
 * Rush Fee amount helper.
 * - Editing (preferStored=false): Subtotal × settings % (live).
 * - Viewing invoice/details/PDF (preferStored=true): use saved rush_fee_amount
 *   so changing settings % does not rewrite existing orders until they are edited.
 */
export function calcRushFeeAmount({
  enabled,
  subtotal,
  percentage,
  storedAmount,
  preferStored = false,
} = {}) {
  if (!enabled) return 0;
  const stored = Number(storedAmount) || 0;
  if (preferStored && stored > 0) {
    return parseFloat(stored.toFixed(2));
  }
  const pct = Number(percentage) || 0;
  const base = Number(subtotal) || 0;
  if (pct > 0) {
    return parseFloat((base * (pct / 100)).toFixed(2));
  }
  return stored > 0 ? parseFloat(stored.toFixed(2)) : 0;
}

export function calcTotalWithRushFee({
  subtotal,
  discount = 0,
  tax = 0,
  rushFee = 0,
} = {}) {
  return Number(
    (
      (Number(subtotal) || 0) -
      (Number(discount) || 0) +
      (Number(tax) || 0) +
      (Number(rushFee) || 0)
    ).toFixed(2),
  );
}

/** Refs for loop-safe rush_fee sync with lead-time condition. */
export function createRushFeeSyncRefs() {
  return {
    primed: false,
    lastKey: null,
    lastLocked: false,
    autoApplied: false,
    /** Override user explicitly turned OFF while locked (keep OFF until condition clears). */
    userDeclinedWhileLocked: false,
    /** User manually turned ON — keep ON until they turn OFF; then condition sync resumes. */
    userManuallyOn: false,
  };
}

/**
 * Record explicit user toggle.
 * - turnedOn:true — stay ON regardless of condition until manual OFF
 * - turnedOn:false — resume condition-based auto ON/OFF
 * Pass declinedWhileLocked when override user turns OFF under lead-time lock.
 */
export function markRushFeeUserToggled(refs, { declinedWhileLocked = false, turnedOn = false } = {}) {
  if (!refs) return;
  if (turnedOn) {
    refs.userManuallyOn = true;
    refs.autoApplied = false;
    refs.userDeclinedWhileLocked = false;
    return;
  }
  refs.userManuallyOn = false;
  refs.autoApplied = false;
  if (declinedWhileLocked) {
    refs.userDeclinedWhileLocked = true;
  } else {
    refs.userDeclinedWhileLocked = false;
  }
}

/**
 * Fingerprint of install dates + product lead times + lock state.
 */
export function getRushFeeLeadConditionKey(values, products, isLocked) {
  const datePart = [
    values?.end,
    values?.start,
    values?.installation_start_date,
    values?.installation_end_date,
  ].join('|');
  const productPart = (products || [])
    .map((p) => {
      const id = p?.product_id ?? p?.id ?? '';
      const lead = Number(p?.lead_time) || 0;
      return `${id}:${lead}`;
    })
    .sort()
    .join(',');
  const lockPart = isLocked == null ? '' : `#L:${isLocked ? 1 : 0}`;
  return `${datePart}#${productPart}${lockPart}`;
}

/**
 * Resolve products, evaluate lock, and sync Formik rush_fee.
 * Returns { productsForLead, isLocked, conditionKey } for UI derivation.
 */
export function runRushFeeLeadSync({
  values,
  products,
  catalog,
  canOverride,
  setFieldValue,
  refs,
  enabled = true,
  /** Edit load: saved rush_fee=0 while locked — keep OFF until condition clears. */
  persistedOffWhileLocked = false,
} = {}) {
  const productsForLead = resolveProductsLeadTime(products, catalog);
  const isLocked = isInstallBeforeRequiredLead(values, productsForLead);
  const conditionKey = getRushFeeLeadConditionKey(values, productsForLead, isLocked);
  syncRushFeeWithLeadCondition({
    conditionKey,
    isLocked,
    canOverride,
    currentRushFee: values?.rush_fee,
    setFieldValue,
    refs,
    enabled,
    persistedOffWhileLocked,
  });
  return { productsForLead, isLocked, conditionKey };
}

/**
 * Sync Formik rush_fee with lead-time lock condition.
 * Auto-OFF only when rush was auto-applied; manual ON persists until manual OFF.
 */
export function syncRushFeeWithLeadCondition({
  conditionKey,
  isLocked,
  canOverride,
  currentRushFee,
  setFieldValue,
  refs,
  enabled = true,
  persistedOffWhileLocked = false,
} = {}) {
  if (!refs || !setFieldValue) return;

  if (!enabled) {
    refs.primed = false;
    refs.lastKey = null;
    refs.lastLocked = false;
    refs.userManuallyOn = false;
    return;
  }

  const rushFeeNum = Number(currentRushFee);
  const locked = !!isLocked;

  const applyLockedOn = () => {
    if (refs.userManuallyOn) return;
    const overrideKeptOff = canOverride && refs.userDeclinedWhileLocked;
    if (!overrideKeptOff && rushFeeNum !== 1) {
      setFieldValue('rush_fee', 1);
    }
    if (!overrideKeptOff) {
      refs.autoApplied = true;
    }
  };

  if (!refs.primed) {
    refs.primed = true;
    refs.lastKey = conditionKey;
    refs.lastLocked = locked;
    if (locked && canOverride && rushFeeNum === 0 && persistedOffWhileLocked) {
      refs.userDeclinedWhileLocked = true;
    }
    if (locked) {
      applyLockedOn();
    } else {
      refs.autoApplied = false;
      if (rushFeeNum === 1) {
        refs.userManuallyOn = true;
      }
    }
    return;
  }

  const keyChanged = refs.lastKey !== conditionKey;
  const lockBecameTrue = locked && !refs.lastLocked;
  refs.lastKey = conditionKey;
  refs.lastLocked = locked;

  if (!keyChanged && !lockBecameTrue) return;

  if (locked) {
    if (lockBecameTrue) {
      refs.userDeclinedWhileLocked = false;
    }
    applyLockedOn();
    return;
  }

  if (refs.autoApplied && !refs.userManuallyOn && rushFeeNum !== 0) {
    setFieldValue('rush_fee', 0);
  }
  if (!refs.userManuallyOn) {
    refs.autoApplied = false;
    refs.userDeclinedWhileLocked = false;
  }
}
